import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  type TikTokFormField,
  type TikTokFormSummary,
  type TikTokMappingRule,
  useTikTokConnection,
} from "@/hooks/useTikTokConnection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { TikTokIcon } from "./BrandIcons";
import { toast } from "sonner";

const TARGETS = [
  ["ignore", "Do not absorb"],
  ["lead.name", "Customer / lead full name"],
  ["lead.email", "Customer / lead email"],
  ["lead.phone", "Customer / lead phone (WhatsApp / SMS)"],
  ["lead.notes", "Customer / lead notes"],
  ["profile.goal", "AI profile: goal / interest"],
  ["profile.preferences", "AI profile: preferences"],
  ["profile.preferred_outlet", "AI profile: preferred outlet"],
  ["crm.company", "CRM: company / organisation"],
  ["analytics.program", "Analytics: programme / service"],
  ["analytics.source", "Analytics: acquisition campaign"],
  ["consent.marketing", "Consent: marketing opt-in"],
] as const;

type Mapping = Record<string, TikTokMappingRule>;

export default function TikTokConnectionCard({
  className,
}: {
  className?: string;
}) {
  const { businessId } = useAuth();
  const tiktok = useTikTokConnection(businessId);
  const [connectOpen, setConnectOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);

  // Connect form state
  const [accessToken, setAccessToken] = useState("");
  const [advertiserId, setAdvertiserId] = useState("");
  const [businessAccountId, setBusinessAccountId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [leadsEnabled, setLeadsEnabled] = useState(true);
  const [messagingEnabled, setMessagingEnabled] = useState(true);

  // Instant Form mapping state
  const [remoteForms, setRemoteForms] = useState<TikTokFormSummary[]>([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [fields, setFields] = useState<TikTokFormField[]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [liveAutomation, setLiveAutomation] = useState(true);
  const [backfillAutomation, setBackfillAutomation] = useState(false);
  const [retentionDays, setRetentionDays] = useState(7);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [stagedConnectionId, setStagedConnectionId] = useState("");

  const hasStableIdentity = useMemo(() => {
    const rules = Object.values(mapping);
    if (rules.length === 0) return true;
    return rules.some(
      (rule) => rule.target === "lead.email" || rule.target === "lead.phone",
    );
  }, [mapping]);

  const activeConnection = useMemo(
    () =>
      tiktok.state?.connections?.find((row) => row.status !== "disconnected"),
    [tiktok.state?.connections],
  );
  const connected = activeConnection?.status === "connected";
  const connectionId = activeConnection?.id ?? stagedConnectionId;

  const act = async (
    action: string,
    input?: Record<string, unknown>,
    success?: string,
  ) => {
    try {
      const data = await tiktok.mutateAsync({ action, input });
      if (success) toast.success(success);
      return data;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "TikTok request failed",
      );
      return undefined;
    }
  };

  const connect = async () => {
    if (!accessToken.trim()) return;
    const data = await act("connect", {
      access_token: accessToken.trim(),
      advertiser_id: advertiserId.trim() || undefined,
      business_account_id: businessAccountId.trim() || undefined,
      display_name: displayName.trim() || undefined,
      lead_gen_enabled: leadsEnabled,
      leads_enabled: leadsEnabled,
      messaging_enabled: messagingEnabled,
    });
    if (!data) return;
    const nextConnId = String(
      data.connection_id ?? data.connection?.id ?? "",
    );
    setStagedConnectionId(nextConnId);
    setAccessToken("");
    setConnectOpen(false);
    toast.success(
      "TikTok connected. Instant Forms & Business Messaging are ready.",
    );
    if (leadsEnabled && nextConnId) {
      if (Array.isArray(data.forms)) {
        setRemoteForms(data.forms as TikTokFormSummary[]);
        setConfigureOpen(true);
      } else {
        const formsRes = await act("list_forms", { connection_id: nextConnId });
        if (formsRes?.forms) {
          setRemoteForms((formsRes.forms ?? []) as TikTokFormSummary[]);
          setConfigureOpen(true);
        }
      }
    }
  };

  const listForms = async () => {
    if (!connectionId) return;
    const data = await act("list_forms", {
      connection_id: connectionId,
    });
    if (!data) return;
    setRemoteForms((data?.forms ?? []) as TikTokFormSummary[]);
    setConfigureOpen(true);
  };

  const loadSchema = async (formId: string) => {
    setSelectedFormId(formId);
    if (!formId) return;
    if (!connectionId) {
      toast.error("The TikTok connection is still loading. Please try again.");
      return;
    }
    setLoadingSchema(true);
    try {
      const data = await act("form_schema", {
        connection_id: connectionId,
        form_id: formId,
      });
      if (!data) return;
      const nextFields = (data?.fields ?? []) as TikTokFormField[];
      setFields(nextFields);
      const configured = tiktok.state?.forms?.find(
        (form) => form.tiktok_form_id === formId,
      );
      setMapping(configured?.field_mapping ?? guessMapping(nextFields));
      if (configured) {
        setLiveAutomation(configured.live_automation_enabled !== false);
        setBackfillAutomation(configured.backfill_automation_enabled === true);
        setRetentionDays(Number(configured.raw_retention_days ?? 7));
      }
    } finally {
      setLoadingSchema(false);
    }
  };

  const configure = async () => {
    if (!selectedFormId || !connectionId) return;
    if (!hasStableIdentity) {
      toast.error(
        "Map at least one TikTok email or phone field so leads resolve to one CRM profile and can receive automated follow-ups.",
      );
      return;
    }
    const selectedForm = remoteForms.find((f) => f.form_id === selectedFormId);
    const data = await act(
      "configure_form",
      {
        connection_id: connectionId,
        form_id: selectedFormId,
        form_name: selectedForm?.form_name ?? `TikTok Form ${selectedFormId}`,
        field_mapping: mapping,
        live_automation_enabled: liveAutomation,
        backfill_automation_enabled: backfillAutomation,
        raw_retention_days: retentionDays,
      },
      "TikTok Instant Form connected and signed webhook configured.",
    );
    if (!data) return;
    setConfigureOpen(false);
  };

  const patchRule = (key: string, patch: Partial<TikTokMappingRule>) => {
    setMapping((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  };

  const disconnect = async () => {
    const result = await act(
      "disconnect",
      {
        connection_id: connectionId,
      },
      "TikTok disconnected and credentials revoked.",
    );
    if (result) setDisconnectOpen(false);
  };

  return (
    <>
      <div
        className={`space-y-4 rounded-xl border bg-card p-4 shadow-sm ${
          className ?? ""
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-foreground">
              <TikTokIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">TikTok</span>
                {connected && (
                  <Badge
                    variant="default"
                    className="border-transparent bg-green-600 text-white hover:bg-green-600"
                  >
                    Connected
                  </Badge>
                )}
                {connected && activeConnection?.messaging_enabled && (
                  <Badge variant="outline">Messaging DMs</Badge>
                )}
                {connected &&
                  (activeConnection?.lead_gen_enabled ??
                    activeConnection?.leads_enabled) && (
                    <Badge variant="outline">Instant Forms</Badge>
                  )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Bring your TikTok DMs into your Dibs inbox so your AI can reply.
                You can also capture leads from TikTok Instant Forms.
              </p>
            </div>
          </div>
          {tiktok.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="flex shrink-0 items-center gap-3">
              <Switch
                checked={connected}
                disabled={tiktok.busy}
                aria-label="TikTok connection"
                onCheckedChange={(checked) => {
                  if (checked) setConnectOpen(true);
                  else setDisconnectOpen(true);
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:bg-muted"
                    aria-label="Manage TikTok connection"
                  >
                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {connected && (
                  <>
                    <DropdownMenuLabel className="font-normal text-muted-foreground">
                      {activeConnection?.display_name ??
                        activeConnection?.advertiser_id ??
                        "Connected"}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </>
                )}
                {connected ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => void listForms()}
                      disabled={tiktok.busy}
                    >
                      Manage Instant Forms
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setDisconnectOpen(true)}
                      disabled={tiktok.busy}
                      className="text-destructive"
                    >
                      Disconnect
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem
                    onClick={() => setConnectOpen(true)}
                    disabled={tiktok.busy}
                  >
                    Connect TikTok
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          )}
        </div>

        {(tiktok.state?.forms?.length ?? 0) > 0 && (
          <div className="space-y-2 border-t pt-3">
            {tiktok.state!.forms.map((form) => (
              <div
                key={form.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-background p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">
                      {form.form_name}
                    </p>
                    <Badge variant="outline">
                      {form.is_enabled ? "Live" : "Paused"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {
                      Object.keys(form.field_mapping ?? {}).filter(
                        (key) => form.field_mapping[key]?.target !== "ignore",
                      ).length
                    }{" "}
                    mapped fields · raw payload retention{" "}
                    {form.raw_retention_days ?? 7} days ·{" "}
                    {form.live_automation_enabled
                      ? "auto follow-up active"
                      : "manual only"}
                  </p>
                  {form.last_error && (
                    <p className="mt-1 text-xs text-destructive">
                      {form.last_error}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Switch
                    checked={form.is_enabled}
                    disabled={tiktok.busy}
                    aria-label={`${form.form_name} webhook`}
                    onCheckedChange={(enabled) =>
                      void act(
                        "toggle_form",
                        {
                          form_connection_id: form.id,
                          is_enabled: enabled,
                        },
                        enabled
                          ? "TikTok Instant Form resumed."
                          : "TikTok Instant Form paused.",
                      )
                    }
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 hover:bg-muted"
                        aria-label={`Manage ${form.form_name}`}
                      >
                        <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          void act(
                            "reconcile",
                            { form_connection_id: form.id },
                            "TikTok lead reconciliation queued.",
                          )
                        }
                        disabled={tiktok.busy}
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Reconcile leads
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}

        {(tiktok.state?.failures?.length ?? 0) > 0 && (
          <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              Needs attention
            </div>
            {tiktok.state!.failures.slice(0, 5).map((failure) => (
              <div
                key={failure.id}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="text-amber-900">
                  {String(failure.status).replace(/_/g, " ")}:{" "}
                  {failure.last_error ??
                    failure.last_error_code ??
                    "Processing stopped"}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      failure.status === "review_required" &&
                      !window.confirm(
                        "Retry this lead only after the conflicting CRM identities have been corrected or merged. Continue?",
                      )
                    ) {
                      return;
                    }
                    void act(
                      "retry_lead",
                      { inbox_id: failure.id },
                      "Lead retry queued.",
                    );
                  }}
                >
                  {failure.status === "review_required"
                    ? "Retry after CRM fix"
                    : "Retry"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect TikTok?</AlertDialogTitle>
            <AlertDialogDescription>
              Dibs will revoke the stored TikTok Vault credentials and stop
              processing Instant Form webhooks and Business Messaging DMs.
              Existing CRM records and conversations will remain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={tiktok.busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void disconnect();
              }}
              disabled={tiktok.busy}
            >
              {tiktok.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect TikTok for Business</DialogTitle>
            <DialogDescription>
              Link your TikTok Business inbox so DMs arrive in Dibs. Customers
              start the chat, and you can reply within 48 hours of their last
              message. Dibs stores tokens in Supabase Vault and never exposes
              them to the browser.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tiktok-access-token">TikTok Access Token</Label>
              <Input
                id="tiktok-access-token"
                type="password"
                autoComplete="off"
                value={accessToken}
                onChange={(event) => setAccessToken(event.target.value)}
                placeholder="Paste your TikTok Business access token"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tiktok-advertiser-id">
                  Advertiser ID (Instant Forms)
                </Label>
                <Input
                  id="tiktok-advertiser-id"
                  autoComplete="off"
                  value={advertiserId}
                  onChange={(event) => setAdvertiserId(event.target.value)}
                  placeholder="e.g. 7123456789012345678"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tiktok-business-account-id">
                  Business Account ID (DMs)
                </Label>
                <Input
                  id="tiktok-business-account-id"
                  autoComplete="off"
                  value={businessAccountId}
                  onChange={(event) => setBusinessAccountId(event.target.value)}
                  placeholder="Optional if same as Advertiser ID"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tiktok-display-name">
                Account Label (optional)
              </Label>
              <Input
                id="tiktok-display-name"
                autoComplete="off"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="e.g. @yourbrand_sg"
              />
            </div>

            <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2">
              <Flag
                label="Enable Business Messaging (DMs)"
                checked={messagingEnabled}
                onChange={setMessagingEnabled}
              />
              <Flag
                label="Enable Instant Forms (Lead Gen)"
                checked={leadsEnabled}
                onChange={setLeadsEnabled}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={connect}
              disabled={!accessToken.trim() || tiktok.busy}
            >
              {tiktok.busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Validate and connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={configureOpen} onOpenChange={setConfigureOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map a TikTok Instant Form into Dibs</DialogTitle>
            <DialogDescription>
              Map TikTok lead fields into the Dibs Customer Graph. When live
              automation is enabled, Dibs automatically follows up via WhatsApp
              when a phone number is present, or falls back to Email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Instant Form</Label>
              <Select value={selectedFormId} onValueChange={loadSchema}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a TikTok Instant Form" />
                </SelectTrigger>
                <SelectContent>
                  {remoteForms.map((form) => (
                    <SelectItem key={form.form_id} value={form.form_id}>
                      {form.form_name} ({form.form_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loadingSchema && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}

            {!loadingSchema && fields.length > 0 && (
              <div className="space-y-2">
                {!hasStableIdentity && (
                  <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      Map at least one phone or email field so Dibs can resolve
                      the lead to a unified Customer Graph contact and trigger
                      automated WhatsApp or Email follow-ups.
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-[minmax(150px,1fr)_minmax(180px,1fr)_auto] gap-3 px-2 text-xs font-medium text-muted-foreground">
                  <span>TikTok field</span>
                  <span>Dibs destination</span>
                  <span>Uses</span>
                </div>
                {fields.map((field) => {
                  const rule = mapping[field.key] ?? { target: "ignore" };
                  return (
                    <div
                      key={field.key}
                      className="grid grid-cols-1 gap-3 rounded-lg border p-3 md:grid-cols-[minmax(150px,1fr)_minmax(180px,1fr)_auto]"
                    >
                      <div>
                        <p className="text-sm font-medium">{field.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {field.type}
                        </p>
                      </div>
                      <Select
                        value={rule.target}
                        onValueChange={(target) =>
                          patchRule(field.key, { target })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TARGETS.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                        <Flag
                          label="CRM"
                          checked={rule.crm_use === true}
                          onChange={(crm_use) =>
                            patchRule(field.key, { crm_use })
                          }
                        />
                        <Flag
                          label="Analytics"
                          checked={rule.analytics_use === true}
                          onChange={(analytics_use) =>
                            patchRule(field.key, { analytics_use })
                          }
                        />
                        <Flag
                          label="AI profile"
                          checked={rule.ai_profile_use === true}
                          disabled={rule.sensitive === true}
                          onChange={(ai_profile_use) =>
                            patchRule(field.key, { ai_profile_use })
                          }
                        />
                        <Flag
                          label="Sensitive"
                          checked={rule.sensitive === true}
                          onChange={(sensitive) =>
                            patchRule(field.key, {
                              sensitive,
                              ...(sensitive ? { ai_profile_use: false } : {}),
                            })
                          }
                        />
                        <Input
                          className="col-span-2 h-7 text-xs"
                          value={rule.workflow_key ?? ""}
                          onChange={(event) =>
                            patchRule(field.key, {
                              workflow_key: event.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9_-]/g, "")
                                .slice(0, 100),
                            })
                          }
                          placeholder="Workflow key: outlet, program, goal"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-3">
              <Flag
                label="Automate WhatsApp/Email follow-up"
                checked={liveAutomation}
                onChange={setLiveAutomation}
              />
              <Flag
                label="Run follow-up during reconciliation"
                checked={backfillAutomation}
                onChange={setBackfillAutomation}
              />
              <div className="space-y-1">
                <Label className="text-xs">Raw payload retention</Label>
                <Select
                  value={String(retentionDays)}
                  onValueChange={(value) => setRetentionDays(Number(value))}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 3, 7, 14, 30].map((days) => (
                      <SelectItem key={days} value={String(days)}>
                        {days} days
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigureOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={configure}
              disabled={
                !selectedFormId ||
                !hasStableIdentity ||
                tiktok.busy ||
                loadingSchema
              }
            >
              {tiktok.busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Save Instant Form mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Flag({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-xs">
      <span className={disabled ? "text-muted-foreground" : ""}>{label}</span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
    </label>
  );
}

function guessMapping(fields: TikTokFormField[]): Mapping {
  const result: Mapping = {};
  for (const field of fields) {
    const label = `${field.key} ${field.label}`.toLowerCase();
    let target = "ignore";
    if (label.includes("email") || field.type.includes("EMAIL")) {
      target = "lead.email";
    } else if (
      label.includes("phone") ||
      label.includes("mobile") ||
      label.includes("whatsapp") ||
      field.type.includes("PHONE")
    ) {
      target = "lead.phone";
    } else if (label.includes("name")) {
      target = "lead.name";
    } else if (label.includes("note") || label.includes("comment")) {
      target = "lead.notes";
    }
    result[field.key] = {
      target,
      label: field.label,
      crm_use: target !== "ignore",
      analytics_use: false,
      ai_profile_use: false,
      sensitive: false,
    };
  }
  return result;
}
